import 'bootstrap/dist/css/bootstrap.css'
import React from "react"
import { createRoot } from "react-dom/client"
import "./styles/base.css"
import "./styles/compat.css"
import "./styles/layout.css"
import "./styles/nav.css"
import App from "./pages/App"
import "./styles/chrome-unified.css"
import { ProvideAuth } from "./hooks/use-auth"
import { BrowserRouter, HashRouter } from "react-router-dom"
import { ApolloClient, ApolloProvider, createHttpLink, InMemoryCache, split } from "@apollo/client"
import { GraphQLWsLink } from "@apollo/client/link/subscriptions"
import { setContext } from "@apollo/client/link/context"
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename"
import { createClient } from "graphql-ws"
import PPUtils from "./utils/PPUtils"
import { getMainDefinition } from "@apollo/client/utilities"
import { isStandalone } from "./config/ppAiConfig"
import { StyleSheetManager } from "styled-components"
import isPropValid from "@emotion/is-prop-valid"
import "./i18nConf"
import PPErrorBoundary from "./components/layout/PPErrorBoundary"

function shouldForwardProp(propName, target) {
    if (typeof target === "string") {
        return isPropValid(propName)
    }
    return true
}

const httpLink = createHttpLink({
    uri: "/graphql"
})

function websocketUrl() {
    if (window.location.hostname.includes("preispiranha01-rosebikescom.msappproxy.net")) {
        return "wss://preispiranha01.rosebikes.it/graphqlinstant"
    }
    if (window.location.hostname === "localhost") {
        return `ws://${window.location.host}/graphqlinstant`
    }
    return `wss://${window.location.hostname}/graphqlinstant`
}

function createWsLink() {
    return new GraphQLWsLink(
        createClient({
            url: websocketUrl,
            lazy: true,
            retryAttempts: Infinity,
            connectionParams: () => ({
                Authorization: "Bearer " + (PPUtils.getLogin()?.token || "")
            })
        })
    )
}

const wsLink = function () {
    if (isStandalone()) {
        return null
    }
    return createWsLink()
}

const splitLink = isStandalone()
    ? httpLink
    : split(
          ({ query }) => {
              const definition = getMainDefinition(query)
              return definition.kind === "OperationDefinition" && definition.operation === "subscription"
          },
          wsLink(),
          httpLink
      )

const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = PPUtils.getLogin()?.token

    // return the headers to the context so httpLink can read them
    return {
        headers: {
            ...headers,
            Authorization: token ? `Bearer ${token}` : ""
        }
    }
})

const removeTypenameLink = removeTypenameFromVariables()

const client = new ApolloClient({
    link: removeTypenameLink.concat(authLink.concat(splitLink)),
    cache: new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    stats: {
                        keyArgs: ["department", "shopIds"],
                        merge(existing, incoming) {
                            if (!incoming) return existing
                            if (!existing) return incoming
                            return { ...existing, ...incoming }
                        }
                    }
                }
            }
        }
    })
})

const origRemoveChild = Node.prototype.removeChild
Node.prototype.removeChild = function (child) {
    if (child && child.parentNode !== this) {
        return child
    }
    return origRemoveChild.call(this, child)
}
const origInsertBefore = Node.prototype.insertBefore
Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
        return this.appendChild(newNode)
    }
    return origInsertBefore.call(this, newNode, referenceNode)
}

const basename = import.meta.env.BASE_URL
const useHashRouter =
    typeof window !== "undefined" &&
    (!window.isSecureContext || window.self !== window.top)
const Router = useHashRouter ? HashRouter : BrowserRouter

createRoot(document.getElementById("root")).render(
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <PPErrorBoundary variant="app">
            <ProvideAuth>
                <ApolloProvider client={client}>
                    <Router
                        basename={basename}
                        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
                    >
                        <App />
                    </Router>
                </ApolloProvider>
            </ProvideAuth>
        </PPErrorBoundary>
    </StyleSheetManager>
)
